import React, { useEffect, useMemo, useState } from "react";
import { Modal, Button, Form, Row, Col } from "react-bootstrap";
import { putCartera } from "../helpers/rutaCarteras";

const pickText = (...vals) => {
  for (const v of vals) {
    const t = String(v ?? "").trim();
    if (t) return t;
  }
  return "";
};

const parseMoney = (v) => {
  const digits = String(v ?? "").replace(/\D/g, "");
  const n = Number(digits);
  return Number.isFinite(n) ? n : 0;
};

const formatMoney = (v) => {
  const n = Number(v);
  if (!Number.isFinite(n) || n <= 0) return "";
  return `$${n.toLocaleString("es-AR")}`;
};
const estadoUi = (v) => {
  const s = String(v || "").toUpperCase().trim();
  return s === "ENDOSADO_A_INGRESAR" ? "EMITIDO_A_INGRESAR" : s;
};

const toYMD = (value) => {
  if (!value) return "";

  if (typeof value === "string") {
    const s = value.trim();

    if (/^\d{4}-\d{2}-\d{2}$/.test(s)) return s;

    if (/^\d{2}[/-]\d{2}[/-]\d{4}$/.test(s)) {
      const [dd, mm, yyyy] = s.split(/[/-]/);
      return `${yyyy}-${mm}-${dd}`;
    }
  }

  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "";

  const local = new Date(d.getTime() - d.getTimezoneOffset() * 60000);
  return local.toISOString().slice(0, 10);
};

const toISOorNull = (yyyy_mm_dd) => {
  if (!yyyy_mm_dd) return null;
  const d = new Date(yyyy_mm_dd + "T00:00:00");
  return Number.isNaN(d.getTime()) ? null : d.toISOString();
};

const cleanOrNull = (s) => {
  const t = String(s ?? "").trim();
  return t ? t : null;
};

export default function ModalCarteraEdit({ show, item, onClose, onSaved }) {
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState("");

  const tipo = useMemo(() => (item?.tipo || "").toUpperCase(), [item]);
  const isCheque = tipo === "CHEQUE";

  const [form, setForm] = useState({
    tipo: "",
    estado: "EN_CARTERA",
    importe: "",
    recibidoEn: "",

    fechaCheque: "",
    nro: "",
    bancoEmisor: "",

    pagadoA: "",
    cuitPagadoA: "",

    cobradoA: "",
    cuitCobradoA: "",

    fechaPago: "",
    observaciones: "",
  });

  useEffect(() => {
    if (!item) return;

    setErr("");

    const t = (item.tipo || "").toUpperCase();

    const fechaCheque = item.fechaCheque || item.fechaPagoDiferido || null;
    const fechaPago = item.fechaPago || item.fechaLiquidacion || item.fechaPagoDiferido || null;

    setForm({
      tipo: t,
      estado: estadoUi(item.estado || "EN_CARTERA"),
      importe: item.importe != null ? formatMoney(item.importe) : "",
      recibidoEn: toYMD(item.recibidoEn),

      fechaCheque: toYMD(fechaCheque),

      nro: pickText(
        item.nro,
        t === "CHEQUE" ? item.nroCheque : item.idICheq,
        item.idICheq,
        item.nroCheque
      ),

      bancoEmisor: pickText(
        item.bancoEmisor,
        item.banco,
        item.bancoGirado,
        item.bancoCheque
      ),

      pagadoA: pickText(
        item.pagadoA,
        item.beneficiario,
        item.aLaOrdenDe
      ),

      cuitPagadoA: pickText(
        item.cuitPagadoA,
        item.cuitBeneficiario
      ),

      cobradoA: pickText(
        item.cobradoA,
        item.librador,
        item.emisor
      ),

      cuitCobradoA: pickText(
        item.cuitCobradoA,
        item.cuitLibrador,
        item.cuitEmisor
      ),

      fechaPago: toYMD(fechaPago),
      observaciones: item.observaciones || "",
    });
  }, [item]);

  const setF = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!item?._id) return;

    setSaving(true);
    setErr("");

    try {
      const t = (form.tipo || "").toUpperCase().trim();
      const nroUnificado = cleanOrNull(form.nro);

      const payload = {
        estado: cleanOrNull(form.estado),
        importe: parseMoney(form.importe),
        recibidoEn: toISOorNull(form.recibidoEn),

        fechaCheque: toISOorNull(form.fechaCheque),
        nro: nroUnificado,

        bancoEmisor: cleanOrNull(form.bancoEmisor),
        banco: cleanOrNull(form.bancoEmisor),

        pagadoA: cleanOrNull(form.pagadoA),
        cuitPagadoA: cleanOrNull(form.cuitPagadoA),

        beneficiario: cleanOrNull(form.pagadoA),
        cuitBeneficiario: cleanOrNull(form.cuitPagadoA),

        cobradoA: cleanOrNull(form.cobradoA),
        cuitCobradoA: cleanOrNull(form.cuitCobradoA),

        fechaPago: toISOorNull(form.fechaPago),
        observaciones: cleanOrNull(form.observaciones),

        ...(t === "CHEQUE"
          ? {
              nroCheque: nroUnificado,
              librador: cleanOrNull(form.cobradoA),
              cuitLibrador: cleanOrNull(form.cuitCobradoA),
              fechaPagoDiferido: toISOorNull(form.fechaPago),
            }
          : {}),

        ...(t === "ICHEQ"
          ? {
              idICheq: nroUnificado,
              emisor: cleanOrNull(form.cobradoA),
              cuitEmisor: cleanOrNull(form.cuitCobradoA),
              fechaLiquidacion: toISOorNull(form.fechaPago),
            }
          : {}),
      };

      await putCartera(item._id, payload);
      onSaved?.();
    } catch (e2) {
      setErr(e2?.response?.data?.msg || e2?.message || "Error guardando cambios");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal show={show} onHide={onClose} size="lg" centered backdrop="static">
      <Form onSubmit={onSubmit}>
        <Modal.Header closeButton={!saving}>
          <Modal.Title>Editar Cartera</Modal.Title>
        </Modal.Header>

        <Modal.Body>
          {err && <div className="alert alert-danger">{err}</div>}

          <Row className="g-3">
            <Col md={3}>
              <Form.Label>Tipo</Form.Label>
              <Form.Control value={form.tipo} disabled />
            </Col>

            <Col md={3}>
              <Form.Label>Estado</Form.Label>
              <Form.Select
                value={form.estado}
                onChange={(e) => setF("estado", e.target.value)}
                disabled={saving}
              >
                <option value="EN_CARTERA">EN_CARTERA</option>
                <option value="DEPOSITADO">DEPOSITADO</option>
                <option value="ACREDITADO">ACREDITADO</option>
                <option value="RECHAZADO">RECHAZADO</option>
                <option value="ANULADO">ANULADO</option>
                <option value="EMITIDO">EMITIDO</option>
                <option value="PAGADO">PAGADO</option>
                <option value="ENDOSADO">ENDOSADO</option>
                <option value="EMITIDO_A_INGRESAR">EMITIDO_A_INGRESAR</option>
                <option value="VENDIDO">VENDIDO</option>
              </Form.Select>
            </Col>

            <Col md={3}>
              <Form.Label>Importe</Form.Label>
              <Form.Control
                type="text"
                inputMode="numeric"
                value={form.importe}
                onChange={(e) => {
                  const digits = e.target.value.replace(/\D/g, "");
                  setF("importe", digits ? `$${Number(digits).toLocaleString("es-AR")}` : "");
                }}
                disabled={saving}
              />
            </Col>

            <Col md={3}>
              <Form.Label>Recibido en</Form.Label>
              <Form.Control
                type="date"
                value={form.recibidoEn}
                onChange={(e) => setF("recibidoEn", e.target.value)}
                disabled={saving}
              />
            </Col>

            <Col md={3}>
              <Form.Label>Fecha cheque</Form.Label>
              <Form.Control
                type="date"
                value={form.fechaCheque}
                onChange={(e) => setF("fechaCheque", e.target.value)}
                disabled={saving}
              />
            </Col>

            <Col md={3}>
              <Form.Label>{isCheque ? "Nro Cheque" : "ID iCheq"}</Form.Label>
              <Form.Control
                value={form.nro}
                onChange={(e) => setF("nro", e.target.value)}
                disabled={saving}
              />
            </Col>

            <Col md={6}>
              <Form.Label>Banco emisor</Form.Label>
              <Form.Control
                value={form.bancoEmisor}
                onChange={(e) => setF("bancoEmisor", e.target.value)}
                disabled={saving}
              />
            </Col>

            <Col md={6}>
              <Form.Label>Pagado a (Beneficiario)</Form.Label>
              <Form.Control
                value={form.pagadoA}
                onChange={(e) => setF("pagadoA", e.target.value)}
                disabled={saving}
              />
            </Col>

            <Col md={3}>
              <Form.Label>CUIT Beneficiario</Form.Label>
              <Form.Control
                value={form.cuitPagadoA}
                onChange={(e) => setF("cuitPagadoA", e.target.value)}
                disabled={saving}
              />
            </Col>

            <Col md={6}>
              <Form.Label>Cobrado a ({isCheque ? "Librador" : "Emisor"})</Form.Label>
              <Form.Control
                value={form.cobradoA}
                onChange={(e) => setF("cobradoA", e.target.value)}
                disabled={saving}
              />
            </Col>

            <Col md={3}>
              <Form.Label>CUIT {isCheque ? "Librador" : "Emisor"}</Form.Label>
              <Form.Control
                value={form.cuitCobradoA}
                onChange={(e) => setF("cuitCobradoA", e.target.value)}
                disabled={saving}
              />
            </Col>

            <Col md={3}>
              <Form.Label>Fecha pago / liquidación</Form.Label>
              <Form.Control
                type="date"
                value={form.fechaPago}
                onChange={(e) => setF("fechaPago", e.target.value)}
                disabled={saving}
              />
            </Col>

            <Col md={9}>
              <Form.Label>Observaciones</Form.Label>
              <Form.Control
                value={form.observaciones}
                onChange={(e) => setF("observaciones", e.target.value)}
                disabled={saving}
              />
            </Col>

            <Col md={12}>
              <div className="text-muted" style={{ fontSize: 12 }}>
                Nota: este formulario guarda campos unificados y también espeja legacy para compatibilidad.
              </div>
            </Col>
          </Row>
        </Modal.Body>

        <Modal.Footer>
          <Button variant="secondary" onClick={onClose} disabled={saving}>
            Cancelar
          </Button>
          <Button variant="primary" type="submit" disabled={saving}>
            {saving ? "Guardando..." : "Guardar"}
          </Button>
        </Modal.Footer>
      </Form>
    </Modal>
  );
}